import { Flex, VStack } from '@chakra-ui/react';
import { motion, useAnimation, useMotionValue } from 'framer-motion';
import _ from 'lodash';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { TrackProps } from './TrackProps';

const transitionProps = {
  stiffness: 400,
  type: 'spring',
  damping: 60,
  mass: 3,
};

const MotionFlex = motion(Flex);

const Track = ({
  direction,
  setIsDragging,
  setTrackIsActive,
  trackIsActive,
  setActiveItem,
  sliderWidth,
  sliderHeight,
  activeItem,
  constraint,
  multiplier,
  itemWidth,
  itemHeight,
  positions,
  gap,
  children,
}: TrackProps) => {
  const [dragStartPosition, setDragStartPosition] = useState(0);
  const controls = useAnimation();
  const y = useMotionValue(0);
  const node = useRef<HTMLDivElement>(null);

  const handleDragStart = () => {
    setIsDragging(true);
    setDragStartPosition(positions[activeItem]);
  };

  const handleDragEnd = (_: any, info: any) => {
    const distance = direction === 'horizontal' ? info.offset.x : info.offset.y;
    const velocity =
      (direction === 'horizontal' ? info.velocity.x : info.velocity.y) *
      multiplier;
    const negOrPos = velocity < 0 || distance < 0 ? 1 : -1;

    const extrapolatedPosition =
      dragStartPosition +
      (negOrPos === 1
        ? Math.min(velocity, distance)
        : Math.max(velocity, distance));

    const closestPosition = positions.reduce((prev, curr) => {
      return Math.abs(curr - extrapolatedPosition) <
        Math.abs(prev - extrapolatedPosition)
        ? curr
        : prev;
    }, 0);

    if (!(closestPosition < positions[positions.length - constraint])) {
      const index = positions.indexOf(closestPosition);
      let offset = 0;
      if (index === positions.length - constraint) {
        offset =
          direction === 'horizontal'
            ? sliderWidth - constraint * (itemWidth + gap)
            : sliderHeight - constraint * (itemHeight + gap);
      }
      if (activeItem === 0 && offset > 0) {
        offset = 0;
      }
      setActiveItem(index);
      controls.start({
        ...(direction === 'horizontal'
          ? {
              x: closestPosition + offset,
            }
          : {
              y: closestPosition + offset,
            }),
        transition: {
          velocity:
            direction === 'horizontal' ? info.velocity.x : info.velocity.y,
          ...transitionProps,
        },
      });
    } else {
      let offset =
        direction === 'horizontal'
          ? sliderWidth - constraint * (itemWidth + gap)
          : sliderHeight - constraint * (itemHeight + gap);
      if (activeItem === 0 && offset > 0) {
        offset = 0;
      }
      setActiveItem(positions.length - constraint);
      controls.start({
        ...(direction === 'horizontal'
          ? {
              x: positions[positions.length - constraint] + offset,
            }
          : {
              y: positions[positions.length - constraint] + offset,
            }),
        transition: {
          velocity:
            direction === 'horizontal' ? info.velocity.x : info.velocity.y,
          ...transitionProps,
        },
      });
    }

    setTimeout(() => {
      setIsDragging(false);
    }, 1);
  };

  const handleResize = useCallback(() => {
    let offset = 0;
    const maxIndex = Math.min(positions.length - constraint, activeItem);
    if (maxIndex === positions.length - constraint) {
      offset =
        direction === 'horizontal'
          ? sliderWidth - constraint * (itemWidth + gap)
          : sliderHeight - constraint * (itemHeight + gap);
    }
    if (maxIndex === 0 && offset > 0) {
      offset = 0;
    }
    controls.start({
      ...(direction === 'horizontal'
        ? {
            x: positions[maxIndex] + offset,
          }
        : {
            y: positions[maxIndex] + offset,
          }),
      transition: {
        ...transitionProps,
      },
    });
  }, [
    activeItem,
    constraint,
    controls,
    direction,
    gap,
    positions,
    sliderWidth,
    sliderHeight,
    itemWidth,
    itemHeight,
  ]);

  const handleClick = useCallback(
    (event: MouseEvent) =>
      node.current?.contains(event.target as Node)
        ? setTrackIsActive(true)
        : setTrackIsActive(false),
    [setTrackIsActive]
  );

  const debouncedSetActiveItem = useMemo(
    () => _.debounce(setActiveItem, 50),
    [setActiveItem]
  );

  const handleWheel = useCallback(
    (event: WheelEvent) => {
      if (direction === 'horizontal') {
        return;
      }
      const delta = event.deltaY;
      if (node.current?.contains(event.target as Node)) {
        if (delta < 0) {
          if (activeItem > 0) {
            // event.preventDefault();
            debouncedSetActiveItem((prev) => prev - 1);
          }
        } else {
          if (activeItem < positions.length - constraint) {
            // event.preventDefault();
            debouncedSetActiveItem((prev) => prev + 1);
          }
        }
      }
    },
    [activeItem, constraint, positions, debouncedSetActiveItem, node]
  );

  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (trackIsActive) {
        if (activeItem < positions.length - constraint) {
          if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
            event.preventDefault();
            setActiveItem((prev) => prev + 1);
          }
        }
        if (activeItem > positions.length - positions.length) {
          if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
            event.preventDefault();
            setActiveItem((prev) => prev - 1);
          }
        }
      }
    },
    [trackIsActive, setActiveItem, activeItem, constraint, positions.length]
  );

  useEffect(() => {
    handleResize();

    document.addEventListener('keydown', handleKeyDown);
    document.addEventListener('mousedown', handleClick);
    document.addEventListener('wheel', handleWheel);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('mousedown', handleClick);
      document.removeEventListener('wheel', handleWheel);
    };
  }, [handleClick, handleResize, handleKeyDown, handleWheel]);

  return (
    <>
      <VStack ref={node} spacing={5} alignItems="stretch">
        <MotionFlex
          flexDirection={direction === 'horizontal' ? 'row' : 'column'}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
          animate={controls}
          style={{ y }}
          drag={direction === 'horizontal' ? 'x' : 'y'}
          _active={{ cursor: 'grabbing' }}
          minWidth="min-content"
          minHeight="min-content"
          flexWrap="nowrap"
          cursor="grab"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ root: node }}
        >
          {children}
        </MotionFlex>
      </VStack>
    </>
  );
};

export default Track;
